import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import { Company, CompanyVendor } from '../classes/models';
import { VendorReview } from '../classes/models/vendor-review';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyVendorService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getCompanyVendor(
    query: { [key: string]: any } = {}
  ): Observable<CompanyVendor> {
    let vendorString = '';
    if (query['VendorId']) {
      vendorString = `vendor/${query['VendorId']}`;
    }
    if (query['ExemptVendorId']) {
      vendorString = `exemptVendor/${query['ExemptVendorId']}`;
    }
    if (query['ClientRegisteredVendorId']) {
      vendorString = `clientRegisteredVendor/${query['ClientRegisteredVendorId']}`;
    }
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get<CompanyVendor>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${query['CompanyId']}/companyVendor/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/${vendorString}/companyVendor/one`,
              { params: query }
            ),

          // Will need to implement this. Admin can use either Client or Vendor route or a custom route for Admins.
          Admin: () =>
            this.http.get(`/api/v2/admin/companyVendor/one`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendors(query = {}): Observable<CompanyVendor[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get<CompanyVendor[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<CompanyVendor[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyVendor[]>(`/api/v2/admin/companyVendor/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyVendor(CompanyVendor: CompanyVendor, query = {}): Observable<any> {
    let vendorString = '';
    if (CompanyVendor.VendorId) {
      vendorString = `vendor/${CompanyVendor.VendorId}`;
    }
    if (CompanyVendor.ExemptVendorId) {
      vendorString = `exemptVendor/${CompanyVendor.ExemptVendorId}`;
    }
    if (CompanyVendor.ClientRegisteredVendorId) {
      vendorString = `clientRegisteredVendor/${CompanyVendor.ClientRegisteredVendorId}`;
    }
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.post(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/company/${CompanyVendor.CompanyId}/companyVendor`,
              CompanyVendor,
              { params: query }
            ),
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/${vendorString}/companyVendor`,
              CompanyVendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/companyVendor`, CompanyVendor, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyVendorPivotColumnHeaders(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/pivotColumnHeaders`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/pivotColumnHeaders`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/companyVendor/pivotColumnHeaders`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveVendorReview(VendorReview: VendorReview): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<Company[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendorReview/save`,
              VendorReview
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyVendorCount(query: {} = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/count`,
              { params: query }
            ),
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/companyVendor/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended(
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<any>(
              `/api/v2/admin/companyVendor/getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyVendorStatusCount(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<any>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/getVendorStatusCounts`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<any>(
              `/api/v2/admin/companyVendor/getVendorStatusCounts`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getCompanyVendorLicenseFileS3PresignedUrl({
    CompanyId,
    VendorId,
    LicenseId
  }: {
    CompanyId: number;
    VendorId: number;
    LicenseId: number;
  }) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<any>(
              `/api/v2/company/${CompanyId}/vendor/${VendorId}/companyVendor/license/${LicenseId}/getCompanyVendorLicenseFileS3PresignedUrl`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
